import { ACCESS_WAITLISTED, WAITLIST_PATH } from '@arthelper/shared/acquisition/strategy';
import { DEFAULT_TOWN_SQUARE_URL } from '~/utils/townSquareLinks';

const GUARD_KEY = 'ah.waitlistRedirectAt';

/** Within this window a second ACCESS_WAITLISTED does not redirect again — the loop guard. */
const GUARD_MS = 30_000;

let redirecting = false;

type ErrorLike = { extensions?: Record<string, unknown> | null };

export function isWaitlistedError(errors: readonly ErrorLike[] | null | undefined): boolean {
  if (!errors) return false;
  return errors.some((error) => error?.extensions?.code === ACCESS_WAITLISTED);
}

function recentlyRedirected(): boolean {
  try {
    const at = Number(window.sessionStorage.getItem(GUARD_KEY));
    return Number.isFinite(at) && Date.now() - at < GUARD_MS;
  } catch {
    return false;
  }
}

function markRedirected(): void {
  try {
    window.sessionStorage.setItem(GUARD_KEY, String(Date.now()));
  } catch {
    // Private mode / quota: the in-memory flag still stops a burst of failing queries.
  }
}

/**
 * The member is still behind the signup gate: hard-redirect to town-square's `/waitlist`,
 * where she is held until enough friends join. Once per page (many queries fail at once),
 * and not again within GUARD_MS — if town-square sends her straight back (it lets her in
 * while this API still says waitlisted) the client stops bouncing instead of looping.
 */
export function redirectToWaitlist(): void {
  if (typeof window === 'undefined' || redirecting || recentlyRedirected()) return;
  // The welcome wizard decides for itself: a held member may be building their profile there
  // (the waiting wizard), and the steps it doesn't run for them can still fail with this code.
  if (window.location.pathname.startsWith('/welcome')) return;
  // Built directly: toTownSquareUrl only knows the chat-link prefixes, and /waitlist is not one.
  const url = `${(__TOWN_SQUARE_URL__ || DEFAULT_TOWN_SQUARE_URL).replace(/\/$/, '')}${WAITLIST_PATH}`;
  redirecting = true;
  markRedirected();
  window.location.assign(url);
}
